import type { FunctionComponent, PropsWithChildren } from 'react'
import { useState } from 'react'
import styled, { css } from 'styled-components'
import { StyledH5 } from '../../stylesNew'
import Chevron from '../../assets/icons/chevronDown.svg'
import Heading from '../Heading/Heading'
import Icon from '../Icon/Icon'
import Button, { Variant } from '../Button/Button'
import useBreakpoint, { Breakpoint } from '../../hooks/useBreakpoint'

const StyledContainer = styled.div<{ $isExpanded: boolean; $isFooter?: boolean }>`
  svg.chevron {
    transform: ${({ $isExpanded }) => ($isExpanded ? 'rotate(180deg)' : 'auto')};
  }

  ${({ $isFooter }) =>
    $isFooter &&
    css`
      margin: 0;
    `}
`

const StyledHeading = styled(StyledH5)<{
  $isExpandable: boolean
  $titleSize?: number
  $color?: string
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  font-size: ${({ $titleSize }) => ($titleSize ? `${$titleSize / 2}rem` : '1.125rem')};
  margin: 0;
  padding: 9px 0 10px;
  cursor: ${({ $isExpandable }) => ($isExpandable ? 'pointer' : 'initial')};
  color: ${({ $color }) => $color ?? 'inherit'};

  @media screen and (min-width: 48rem) {
    font-size: ${({ $titleSize }) => ($titleSize ? `${$titleSize}rem` : '1.5rem')};
    padding: 24px 0 12px;
  }
  svg {
    width: 1.5rem;
    fill: ${({ color }) => color ?? 'var(--dark)'};
  }
`

const FooterHeading = styled(Heading)<{ $open: boolean }>`
  display: flex;
  align-items: center;
  color: var(--light);
  margin-bottom: 1.5rem;
  cursor: pointer;
  svg {
    fill: var(--light);
    margin-right: 0.5rem;
  }
  &:focus {
    text-decoration: ${({ $open }) => ($open ? 'underline' : 'none')};
  }

  @media screen and (min-width: 48rem) {
    svg {
      display: none;
    }
    text-decoration: none;
  }
`

interface AccordionBlockProps {
  id?: string
  title?: string
  titleSize?: number
  isExpandable?: boolean
  open?: boolean
  isFooter?: boolean
  color?: string
}

const AccordionBlock: FunctionComponent<PropsWithChildren<AccordionBlockProps>> = ({
  id = undefined,
  title,
  titleSize,
  children,
  isExpandable = true,
  open = false,
  isFooter,
  color,
  ...otherProps
}) => {
  const showDesktopVariant = useBreakpoint({ minBreakpoint: Breakpoint.Md })
  const [isExpanded, setIsExpanded] = useState(open)

  return (
    <StyledContainer id={id} $isExpanded={isExpanded} $isFooter={isFooter} {...otherProps}>
      {title && isFooter ? (
        <Button
          variant={Variant.Hidden}
          onClick={() => (showDesktopVariant ? null : setIsExpanded(isExpandable && !isExpanded))}
        >
          <FooterHeading type="h4" $open={isExpanded} data-testid="footer-heading">
            <Icon size={20}>
              <Chevron className="chevron" />
            </Icon>
            {title}
          </FooterHeading>
        </Button>
      ) : (
        <StyledHeading
          $isExpandable={isExpandable}
          $titleSize={titleSize}
          onClick={() => setIsExpanded(isExpandable && !isExpanded)}
          data-testid="heading"
          $color={color}
        >
          {title}
          {isExpandable && <Chevron className="chevron" />}
        </StyledHeading>
      )}
      {((isExpandable && isExpanded) || !isExpandable) && (
        <div data-testid={isFooter ? 'expandedFooterRow' : 'expandedRow'}>{children}</div>
      )}
    </StyledContainer>
  )
}

export default AccordionBlock
